<template>
  <v-container>
    <!-- If we're editing a location -->
    <v-layout row wrap v-if="location.editing && $can('edit_location')">
      <v-flex xs12 md8 offset-md2>
        <h1 class="primary--text mb-4">Edit Location - {{ location.cached.name }}</h1>
        <app-alert
          class="mb-3"
          type="error"
          :message="location.error"
          @dismissed="location.error = false"
        />

        <v-card>
          <v-card-title class="subheading font-weight-bold px-4">
            <span>Update Location</span>
          </v-card-title>
          <v-divider></v-divider>

          <v-container class="px-4" grid-list-lg>
            <v-form ref="form" @submit.prevent="updateLocation">
              <v-layout justify-space-between row wrap>
                <v-flex xs12 sm6>
                  <v-text-field
                    label="Name"
                    type="text"
                    v-model="location.cached.name"
                    @input="location.canSave = true"
                    :rules="[rules.isRequired]"
                    required
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-select
                    label="Type"
                    v-model="location.cached.type"
                    @input="location.canSave = true"
                    :items="typeOptions"
                    :rules="[rules.isRequired]"
                    required
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-select
                    label="State"
                    v-model="location.cached.state_id"
                    @change="loadAreas"
                    @input="resetAddress"
                    :items="stateOptions"
                    :rules="[rules.isRequired]"
                    :loading="statesLoading"
                    :disabled="statesLoading"
                    required
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6>
                  <v-select
                    label="Local Government"
                    v-model="location.cached.tag"
                    @input="resetAddress"
                    :items="areaOptions"
                    :rules="[rules.isRequired]"
                    :loading="areasLoading"
                    :disabled="!areaOptions.length"
                    required
                  ></v-select>
                </v-flex>
                <v-flex xs12>
                  <div class="v-input v-text-field theme--light primary--text mt-0">
                    <div class="v-input__control">
                      <div class="v-input__slot">
                        <div class="v-text-field__slot">
                          <gmap-autocomplete
                            class="input-location"
                            name="location"
                            :placeholder="location.cached.address"
                            @focus="location.cached.address = ''"
                            @input="location.canSave = true"
                            @place_changed="setAddress"
                            autocomplete="off"
                            required
                          ></gmap-autocomplete>
                        </div>
                      </div>
                      <div class="v-text-field__details" v-if="!isAddressValid">
                        <div class="v-messages theme--light error--text">
                          <div class="v-messages__wrapper">
                            <div class="v-messages__message">Map address is required</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-flex>
              </v-layout>

              <v-layout class="mt-3 mb-3">
                <v-btn
                  depressed
                  color="accent"
                  type="submit"
                  :disabled="!location.canSave || location.isSaving"
                >
                  Save
                  <v-progress-circular
                    class="ml-2"
                    :indeterminate="true"
                    :size="18"
                    :width="2"
                    v-if="location.isSaving"
                  ></v-progress-circular>
                </v-btn>
                <v-btn flat @click="cancelEdit">Cancel</v-btn>
              </v-layout>
            </v-form>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- If we are not, everything else can happen -->
    <v-layout row wrap class="mb-5" v-else>
      <v-flex xs12 md12>
        <v-layout align-center>
          <h1 class="primary--text mr-3">Locations</h1>
          <v-btn
            small
            depressed
            color="accent"
            to="/locations/create"
            v-if="$can('create_location')"
          >
            <v-icon small>add</v-icon>Create Location
          </v-btn>
          <v-spacer></v-spacer>
          <v-text-field v-model="search" append-icon="search" label="Search" single-line></v-text-field>
        </v-layout>
      </v-flex>
    </v-layout>

    <app-alert
      class="mb-3"
      type="success"
      message="Location successfully created."
      v-if="$route.query.locationCreated && !location.editing"
    />
    <!-- <app-alert class="mb-3" type="success" :message="location.updated" /> -->

    <v-layout row wrap v-if="!location.editing">
      <v-flex xs12>
        <v-card>
          <v-card-title class="subheading font-weight-bold px-4">
            <span v-if="search !== ''">Results for "{{ search }}"</span>
            <span v-else>{{ tableTitle }}</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="headers"
            :items="filteredLocations"
            :loading="locationsLoading"
            :search="search"
            :rows-per-page-items="[30, 60, 100]"
            no-data-text="No locations available"
            :hide-actions="locations.length < 6"
          >
            <template v-slot:items="props">
              <td> {{ (props.index + 1) }} </td>
              <td class="py-3">{{ props.item.name }}</td>
              <td>{{ props.item.state }}</td>
              <td>{{ props.item.tag }}</td>
              <td class="py-3">{{ props.item.address }}</td>
              <td>
                <v-chip
                  small
                  :color="props.item.status === 'active' ? 'green' : 'red'"
                  text-color="white"
                >{{ $lang(props.item.status) }}</v-chip>
              </td>
              <td>
                <v-icon
                  small
                  class="mr-2"
                  @click="editMode(props.item)"
                  v-if="$can('edit_location')"
                >edit</v-icon>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import '@/plugins/google-maps'
import { mapGetters } from 'vuex'
import { LOCATION_FETCH, LOCATION_UPDATE } from '@/store/actions/location'
import { STATE_FETCH } from '@/store/actions/state'
import { statusOptions } from '@/data/options'
import rolesData from '@/data/roles'

export default {
  name: 'Locations',
  created () {
    this.statesLoading = true
    this.locationsLoading = true
    this.$store.dispatch(LOCATION_FETCH)
      .finally(() => (this.locationsLoading = false))
    this.$store.dispatch(STATE_FETCH)
      .finally(() => (this.statesLoading = false))
  },
  watch: {
    /**
      * Watch 'location.editing' and load areas loaction state
      * @param {Object} value Updated data of the location
      */
    'location.editing' (value) {
      if (value !== null) {
        this.loadAreas()
      }
    }
  },
  data () {
    return {
      search: this.$route.query.s || '',
      headers: [
        { text: '#', value: 'index', sortable: false },
        { text: 'Name', value: 'name' },
        { text: 'State', value: 'state' },
        { text: 'LGA', value: 'tag' },
        { text: 'Address', value: 'address' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', value: 'actions', sortable: false }
      ], 
      location: {
        editing: null,
        cached: null,
        updated: false,
        canSave: false,
        error: false,
        isSaving: false
      },
      locationsLoading: false,
      areaOptions: [],
      areasLoading: false,
      statusOptions: statusOptions,
      statesLoading: false,
      typeOptions: [
        { text: 'Camp', value: 'camp' },
        { text: 'Community', value: 'community' }
      ],
      rules: {
        isRequired: value => this.$validator.isRequired(value)
      }
    }
  },
  methods: {
    resetAddress () {
      this.location.canSave = true
      this.location.cached.address = ''
    },
    setAddress (place) {
      this.location.cached.address = place.formatted_address
      this.location.cached.latitude = place.geometry.location.lat()
      this.location.cached.longitude = place.geometry.location.lng()
    },
    loadAreas () {
      this.areasLoading = true
      const stateName = this.$store.getters.stateName(this.location.cached.state_id)
      if (stateName !== '') {
        //this.$http.get(`http://locationsng-api.herokuapp.com/api/v1/states/${stateName}/lgas`)
        this.$http.get(`https://api.csv49.xyz/csv8/state/${stateName}`)
          .then((response) => {
            const data = response.data.data
            
            this.areasLoading = false
            this.areaOptions = !data.length ? [] : data.map(lga => ({ text: lga, value: lga }))
          })
      }
    },
    editMode (location) {
      this.location.canSave = false
      this.location.updated = false
      this.location.editing = location._id
      this.location.cached = Object.assign({}, location)
      delete this.location.cached.created // removes the created timestamp
      delete this.location.cached.modified // removes the modified timestamp
    },
    updateLocation () {
      if (!this.$refs.form.validate() || !this.isAddressValid) return

      this.location.isSaving = true
      this.$store.dispatch(LOCATION_UPDATE, this.location.cached)
        .then(() => {
          this.location.updated = 'Location has been successfully updated.'
        })
        .finally(() => {
          this.location.canSave = false
          this.location.editing = null
          this.location.cached = null
          this.location.isSaving = false
        })
    },
    cancelEdit () {
      this.location.canSave = false
      this.location.editing = null
      this.location.cached = null
    }
  },
  computed: {
    stateOptions () {
      let stateOptions = this.$store.getters.statesAsOptions
      // If state agent, show only agents state in list
      if (this.currentUser.parent && this.currentUser.parent === 'state') {
        const state = stateOptions.find(stateOption => this.currentUser.parent_id === stateOption.value)
        stateOptions = [state]
      }
      return stateOptions
    },
    tableTitle () {
      let title = ''
      if (this.currentUser.role === rolesData.admin || rolesData.agencyAgents.includes(this.currentUser.role)) {
        title = 'All Locations'
      }
      else {
        title = `All Locations in ${this.$store.getters.stateName(this.currentUser.parent_id)}`
      }
      return title
    },
    filteredLocations () {
      let locations = this.locations
      if (this.currentUser.role === rolesData.admin || rolesData.agencyAgents.includes(this.currentUser.role)) {
        locations = this.locations
      }
      else {
        locations = this.$store.getters.locationsByField('state_id', this.currentUser.parent_id)
      }
      return locations
    },
    isAddressValid () {
      return this.location.cached.address.trim() !== ''
    },
    ...mapGetters({
      locations: 'locations',
      currentUser: 'currentUser'
    })
  }
}
</script>
